import api from './client';

export type BillingCycle = 'monthly' | 'yearly';

export interface SubscriptionPlan {
  id: number;
  name: string;
  description: string | null;
  /** Sequelize DECIMAL ships as a string over JSON. */
  price: string | number;
  billingCycle: BillingCycle;
  features: string[];
  isMostPopular: boolean;
  isActive: boolean;
  displayOrder: number;
  /** Max listings allowed on this plan. null = unlimited. */
  totalListing: number | null;
  /** Free trial length in days. 0 = no trial. */
  trialDays: number;
  stripeProductId?: string | null;
  stripeMonthlyPriceId?: string | null;
  stripeYearlyPriceId?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface SubscriptionPlanInput {
  name: string;
  description?: string;
  price?: number | string | null;
  billingCycle?: BillingCycle;
  features?: string[];
  isMostPopular?: boolean;
  isActive?: boolean;
  displayOrder?: number;
  /** Max listings allowed on this plan. null = unlimited. */
  totalListing?: number | null;
  /** Free trial length in days. 0 = no trial. */
  trialDays?: number;
}

interface ListResponse {
  status: number;
  total: number;
  active: number;
  stripeEnabled?: boolean;
  stripeMode?: 'test' | 'live';
  plans: SubscriptionPlan[];
}

/** What happened when the plan was pushed to Stripe. */
export interface PlanStripeSync {
  ok: boolean;
  /** 'free' = nothing to create; 'not_configured' = no Stripe keys saved. */
  skipped?: 'free' | 'not_configured';
  productId?: string;
  priceId?: string;
  error?: string;
}

interface MutationResponse {
  status: number;
  message?: string;
  error?: string | string[];
  plan?: SubscriptionPlan;
  stripe?: PlanStripeSync;
}

/** GET /api/plans */
export const listSubscriptionPlans = async (opts?: {
  active?: boolean;
  billingCycle?: BillingCycle;
}): Promise<{
  plans: SubscriptionPlan[];
  total: number;
  active: number;
  stripeEnabled: boolean;
  stripeMode: 'test' | 'live';
}> => {
  const params: Record<string, string> = {};
  if (opts?.active === true) params.active = 'true';
  if (opts?.active === false) params.active = 'false';
  if (opts?.billingCycle) params.billingCycle = opts.billingCycle;
  const res = await api.get<ListResponse>('/plans', { params });
  return {
    plans: res.data.plans,
    total: res.data.total,
    active: res.data.active,
    stripeEnabled: !!res.data.stripeEnabled,
    stripeMode: res.data.stripeMode === 'live' ? 'live' : 'test',
  };
};

/** POST /api/plans (admin) */
export const createSubscriptionPlan = async (
  payload: SubscriptionPlanInput
): Promise<SubscriptionPlan> => {
  const res = await api.post<MutationResponse>('/plans', payload);
  if (!res.data.plan) throw new Error('No plan returned');
  return res.data.plan;
};

/**
 * PUT /api/plans/:id (admin). Also pushes the plan to Stripe; a price change
 * is rejected (and nothing saved) if Stripe can't create the new price.
 */
export const updateSubscriptionPlan = async (
  id: number,
  payload: SubscriptionPlanInput
): Promise<{ plan: SubscriptionPlan; stripe: PlanStripeSync | null }> => {
  const res = await api.put<MutationResponse>(`/plans/${id}`, payload, { timeout: 45000 });
  if (!res.data.plan) throw new Error('No plan returned');
  return { plan: res.data.plan, stripe: res.data.stripe ?? null };
};

/** POST /api/plans/:id/sync-stripe (admin) — create/repair the Stripe product and price. */
export const syncSubscriptionPlanToStripe = async (
  id: number
): Promise<{ plan: SubscriptionPlan; stripe: PlanStripeSync }> => {
  const res = await api.post<MutationResponse>(`/plans/${id}/sync-stripe`, undefined, { timeout: 45000 });
  if (!res.data.plan || !res.data.stripe) throw new Error('No plan returned');
  return { plan: res.data.plan, stripe: res.data.stripe };
};

/** PATCH /api/plans/:id/toggle (admin) */
export const toggleSubscriptionPlan = async (
  id: number
): Promise<SubscriptionPlan> => {
  const res = await api.patch<MutationResponse>(`/plans/${id}/toggle`);
  if (!res.data.plan) throw new Error('No plan returned');
  return res.data.plan;
};

/** DELETE /api/plans/:id (admin) */
export const deleteSubscriptionPlan = async (id: number): Promise<void> => {
  await api.delete<MutationResponse>(`/plans/${id}`);
};

/** Format DECIMAL price for display, treating 0 as Free. */
export const formatPlanPrice = (
  price: SubscriptionPlan['price'],
  cycle: BillingCycle
): { amount: string; suffix: string } => {
  const num = typeof price === 'number' ? price : Number(price);
  if (!Number.isFinite(num) || num <= 0) {
    return { amount: '$0', suffix: '' };
  }
  const formatted = num % 1 === 0 ? `$${num}` : `$${num.toFixed(2)}`;
  return { amount: formatted, suffix: cycle === 'monthly' ? '/month' : '/year' };
};

/** Renewing subscribers on a plan, and how many pay an older price. */
export interface PlanSubscriberSummary {
  planId: number;
  price: number;
  subscribers: number;
  onOlderPrice: number;
}

export interface MoveSubscribersResult {
  moved: number;
  alreadyCurrent: number;
  skipped: number;
  failed: { subscriptionId: number; error: string }[];
}

/** GET /api/plans/:id/subscribers (admin) */
export const getPlanSubscriberSummary = async (id: number): Promise<PlanSubscriberSummary> => {
  const res = await api.get<{ status: number; summary: PlanSubscriberSummary }>(`/plans/${id}/subscribers`);
  return res.data.summary;
};

/**
 * POST /api/plans/:id/move-subscribers (admin) — move renewing subscribers to
 * the plan's current price from their next payment. One Stripe call per
 * subscriber, so allow plenty of time.
 */
export const movePlanSubscribers = async (
  id: number
): Promise<{ message: string; result: MoveSubscribersResult; summary: PlanSubscriberSummary }> => {
  const res = await api.post<{
    status: number;
    message: string;
    result: MoveSubscribersResult;
    summary: PlanSubscriberSummary;
  }>(`/plans/${id}/move-subscribers`, undefined, { timeout: 120000 });
  return { message: res.data.message, result: res.data.result, summary: res.data.summary };
};
